/* eslint-disable @typescript-eslint/no-unused-expressions */
import {
    Box,
    TextField,
    Button,
    Typography,
    Link,
    Stack,
    Chip,
    Pagination,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ModalUkmField, ParamsGetUkm } from "../../constants/types";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import useUkm from "../../services/queries/useUkm";
import useUser from "../../services/queries/useUser";
import Spinner from "../Spinner/Spinner";
import ModalUkm from "../ModalUkm/ModalUkm";
import ModalAlertPro from "../ModalAlertPro/ModalAlertPro";
import CardInfoToko from "./CardInfoToko";
import { getLocalUkmId, setLocalUkmId } from "../../utils/handleChangeUkmId";
import Select, { StylesConfig } from "react-select";

interface IInfoTokoProps {}

interface IOption {
    value: number;
    label: string;
}

const paginatedParams: ParamsGetUkm = {
    size: 4,
    page: 1,
    search: "",
};

const allUkmParams: ParamsGetUkm = {
    size: 500,
    page: 1,
    search: "",
};

const InfoToko = (props: IInfoTokoProps) => {
    const { data: user, refetch: refetchUser } = useUser();
    const [paramsUkm, setParamsUkm] = useState<ParamsGetUkm>(paginatedParams);
    const {
        data: ukm,
        status: statusUkm,
        refetch: refetchUkm,
        isLoading: isLoadingUkm,
    } = useUkm(paramsUkm);
    const { data: allUkm } = useUkm(allUkmParams);
    const [search, setSearch] = useState("");
    const optionsUkm = useMemo(
        () =>
            allUkm?.content.map((ukms) => ({
                value: ukms.id,
                label: ukms.namaToko,
            })) ?? [],
        [allUkm?.content],
    );
    const [isOpenModalAlertPro, setIsOpenModalAlertPro] = useState(false);

    const ukmId = useMemo(() => {
        if (user?.userId) {
            return getLocalUkmId(user?.userId);
        }
        return null;
    }, [user?.userId]);

    const defaultSelected = optionsUkm.find((li) => li.value === ukmId);
    const [selectUkm, setSelectUkm] = useState<IOption | undefined>(
        defaultSelected,
    );

    useEffect(() => {
        if (ukmId) {
            setSelectUkm(optionsUkm.find((li) => li.value === ukmId));
        } else if (optionsUkm.length > 0) {
            setSelectUkm(optionsUkm[0]);
        }
    }, [optionsUkm, ukmId]);

    const ukmData = useMemo(() => {
        if (ukmId) {
            return allUkm?.content.find((li) => li.id === ukmId);
        }
        return allUkm?.content[0];
    }, [allUkm?.content, ukmId]);

    const customStyles: StylesConfig<IOption> = useMemo(
        () => ({
            control: (base, state) => ({
                ...base,
                background: "#FFFF",
                borderRadius: 8,
                borderColor: state.isFocused ? ukmData?.warna : "#d4d4d4",
                borderWidth: 1,
                boxShadow: undefined,
                "&:hover": {
                    borderColor: state.isFocused ? ukmData?.warna : "black",
                },
            }),
            option: (styles, { isDisabled, isFocused, isSelected }) => ({
                ...styles,
                backgroundColor: isDisabled
                    ? undefined
                    : isSelected || isFocused
                      ? "#f5f5f5"
                      : undefined,
                color: isDisabled ? "#ccc" : "black",
                cursor: isDisabled ? "not-allowed" : "default",
                ":active": {
                    ...styles[":active"],
                    backgroundColor: !isDisabled
                        ? isSelected
                            ? "black"
                            : "white"
                        : undefined,
                },
            }),
            input: (styles) => ({ ...styles, width: 150 }),
            placeholder: (styles) => ({ ...styles }),
            singleValue: (styles) => ({
                ...styles,
                borderBlockEndWidth: 0,
            }),
        }),
        [ukmData?.warna],
    );

    const handleSearchSubmit = useCallback(() => {
        setParamsUkm((prev) => ({
            ...prev,
            search: search,
            page: 1,
        }));
    }, [search]);

    const handlePageChange = useCallback(
        (_event: React.ChangeEvent<unknown>, value: number) => {
            setParamsUkm((prev) => ({
                ...prev,
                page: value,
            }));
        },
        [],
    );

    const handleSelectChange = useCallback((option: IOption | null) => {
        if (!option) {
            return;
        }
        setSelectUkm(option);
    }, []);

    const handleSaveSelectedUkm = useCallback(() => {
        if (user?.countProUkm === 0 || !selectUkm?.value) {
            return;
        }
        if (user?.userId) {
            setLocalUkmId(user.userId, selectUkm.value);
            window.location.reload();
        }
    }, [selectUkm?.value, user?.countProUkm, user?.userId]);

    const paginationCount = useMemo(() => {
        if (!ukm?.totalElements) {
            return 1;
        }
        return Math.max(1, Math.ceil(ukm.totalElements / paginatedParams.size));
    }, [ukm?.totalElements]);

    const [modalUkmState, setModalUkmState] = useState<{
        open: boolean;
        data?: ModalUkmField;
    }>({
        open: false,
        data: undefined,
    });

    const handleOpenModalUkm = useCallback((data?: ModalUkmField) => {
        setModalUkmState({
            open: true,
            data,
        });
    }, []);

    const handleCloseModalUkm = useCallback(() => {
        setModalUkmState({
            open: false,
            data: undefined,
        });
    }, []);

    if (statusUkm === "loading") {
        return (
            <Stack
                display="grid"
                gridTemplateColumns="1fr"
                alignItems="center"
                height={400}
                position="relative"
            >
                <Spinner />
            </Stack>
        );
    }

    return (
        <React.Fragment>

            <Stack
                direction="column"
                spacing={0.5}
                marginBottom={2}
            >
                <Typography variant="h6" fontWeight="bold">
                    Informasi UKM
                </Typography>

                <Typography>
                    Atur dan kelola detail informasi dan langganan UKM.
                </Typography>

            </Stack>

            <Stack
                direction="row"
                spacing={2}
                marginBottom={2}
                alignItems="center"
            >
                <Typography variant="h6" fontWeight="bold">
                    Pindah UKM
                </Typography>
                {user && user?.countProUkm > 0 ? null : (
                    <Chip
                        label="Standar"
                        size="small"
                        onClick={() => setIsOpenModalAlertPro(true)}
                    />
                )}
            </Stack>
            <Stack
                component={user?.countProUkm === 0 ? Link : Stack}
                onClick={() => {
                    if (user && user?.countProUkm >= 1) {
                        return;
                    }
                    setIsOpenModalAlertPro(true);
                }}
                underline="none"
                direction="column"
                spacing={1}
                marginTop={2}
                sx={{
                    width: { xs: 300, sm: 400 },
                    cursor: user?.countProUkm === 0 ? "pointer" : undefined,
                }}
            >
                <Select
                    styles={customStyles}
                    value={selectUkm}
                    options={optionsUkm}
                    onChange={(option) =>
                        handleSelectChange(option as IOption | null)
                    }
                    isLoading={isLoadingUkm}
                    isDisabled={user?.countProUkm === 0}
                    components={{
                        IndicatorSeparator: () => null,
                    }}
                    menuPlacement="auto"
                />
                <Button
                    disabled={user?.countProUkm === 0}
                    variant="contained"
                    onClick={handleSaveSelectedUkm}
                >
                    Simpan Perubahan
                </Button>
            </Stack>
            <Typography
                variant="h6"
                marginBottom={2}
                marginTop={8}
                fontWeight="bold"
            >
                Pengaturan UKM
            </Typography>
            <Stack
                display="flex"
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                marginBottom={2}
            >
                <TextField
                    placeholder="Cari nama ukm..."
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            handleSearchSubmit();
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <Link
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                onClick={handleSearchSubmit}
                                sx={{ cursor: "pointer" }}
                            >
                                <SearchIcon fontSize="small" />
                            </Link>
                        ),
                        style: {
                            backgroundColor: "white",
                        },
                    }}
                    variant="outlined"
                />
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenModalUkm()}
                    sx={{ display: "none", height: "40px" }}
                >
                    Tambah UKM
                </Button>
            </Stack>
            <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
                gap={3}
            >
                {ukm && ukm.totalElements > 0 ? (
                    ukm.content.map((li) => (
                        <CardInfoToko
                            key={li.id}
                            id={li.id}
                            data={li}
                            onOpenModalUkm={handleOpenModalUkm}
                        />
                    ))
                ) : (
                    <Box
                        display="flex"
                        flex={1}
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        height="100%"
                    >
                        <Typography variant="h5" fontWeight="bold">
                            UKM tidak ditemukan
                        </Typography>
                    </Box>
                )}
            </Box>
            <Box display="flex" justifyContent="center" paddingY={2}>
                <Pagination
                    color="primary"
                    count={paginationCount}
                    page={paramsUkm.page}
                    onChange={handlePageChange}
                    siblingCount={0}
                    // sx={{ justifyContent: "center" }}
                />
            </Box>
            <ModalUkm
                refetchUkm={refetchUkm}
                refetchUser={refetchUser}
                open={modalUkmState.open}
                onClose={handleCloseModalUkm}
                data={modalUkmState.data}
            />
            {ukmData && (
                <ModalAlertPro
                    isOpenModalAlertPro={isOpenModalAlertPro}
                    closeModalAlertPro={() => setIsOpenModalAlertPro(false)}
                    dataUkm={{ id: ukmData?.id, nama: ukmData?.namaToko }}
                />
            )}
        </React.Fragment>
    );
};

export default InfoToko;
